// Tiny adapter hook that turns a local `open` boolean into a
// transition-edge broadcast on `BUG_REPORT_PANEL_STATE_EVENT`.
// Mounted once per bug-report entry component (desktop pill /
// mobile sandwich) so the activity dock can observe panel
// open/close events without either side importing the other.
//
// Edge-triggered on purpose: we only fire when `open` differs from
// the previous render. This keeps subscribers from having to dedup,
// and matches the behavior of `useActivityDockKeyboardCollapse` /
// other dock lifecycle hooks that explicitly avoid spamming
// no-op events.
//
// See docs/planning/bug_report_placement_v2.md "Open follow-ups"
// (resolved 2026-05-31) and `src/lib/bugReportPanelOpenEvent.ts`
// for the receiver-side decision rules.

import { useEffect, useRef } from "react";
import {
  emitBugReportPanelStateEvent,
  type BugReportPanelSource,
} from "@/lib/bugReportPanelOpenEvent";

export function useBugReportPanelOpenBroadcast(
  open: boolean,
  source: BugReportPanelSource,
): void {
  const lastOpenRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (lastOpenRef.current === open) return;
    lastOpenRef.current = open;
    emitBugReportPanelStateEvent({ open, source });
  }, [open, source]);
}
